// import { ProgramSkeleton } from "~/routes/terminals+/$id._index";
import { CardDataTableSkeleton, DataTableSkeleton } from "./ui/data-table";
import { NavLink, useNavigate } from "react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { Terminal } from "~/lib/db/schema";
import { Icon } from "./ui/icon";
import { cn } from "~/lib/utils";
import { getIsAdmin, TeamRoles, useRoles } from "~/lib/utils/roles";
import { useTableSkeletonState } from "~/lib/hooks/use-table-skeleton-state";
import { Skeleton } from "./ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { useMemo } from "react";
import { ChartContainer } from "./ui/chart";
import { DollarSign } from "lucide-react";
import { Bar, BarChart } from "recharts";
import { RecentSaleSkeleton } from "./features/dashboard/owner-dashboard/recent-sales-table";

export const DashboardRouteSkeleton = () => {
  const { teamRole } = useRoles();

  switch (teamRole) {
    case TeamRoles.OWNER:
      return <OwnerDashboardSkeleton />;

    case TeamRoles.ADMIN:
    case TeamRoles.MANAGER:
    case TeamRoles.CONTRIBUTOR:
      return <AdminDashboardSkeleton />;

    case TeamRoles.MONITOR:
    case TeamRoles.OPERATOR:
      return <DefaultSkeleton />;

    case TeamRoles.VIEWER:
    default:
      return <DefaultSkeleton />;
  }
};

export const OwnerDashboardSkeleton = () => {
  const axisMonthToLabel: Record<string, string> = {
    "01": "Ene",
    "02": "Feb",
    "03": "Mar",
    "04": "Abr",
    "05": "May",
    "06": "Jun",
    "07": "Jul",
    "08": "Ago",
    "09": "Sep",
    "10": "Oct",
    "11": "Nov",
    "12": "Dic",
  };

  const placeholderData = useMemo(() => {
    return Object.keys(axisMonthToLabel).map((month) => ({
      month,
      value: Math.floor(5 + Math.random() * 100), // Random values between 0-100
    }));
  }, []);

  return (
    <div className="flex flex-col gap-6 md:gap-6 p-6 md:px-20 md:py-14">
      <div className="flex flex-col md:flex-row gap-6 justify-between">
        <h1 className="text-h1">Dashboard</h1>

        <div className="flex flex-col-reverse md:flex-row gap-4 md:gap-2">
          <div className="flex items-center justify-center border border-border rounded-md h-10 px-4">
            <Skeleton className="h-4 w-full md:w-48" />
          </div>
          <Skeleton className="bg-foreground/10 h-10 w-full md:w-48" />
        </div>
      </div>

      <div className="flex flex-col gap-4 h-full md:h-[80vh]">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row justify-between space-y-0 pb-2">
              <CardTitle className="text-sm">Total ingresos</CardTitle>
              <Icon name="dollar-sign" className="text-muted-foreground" />
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5 h-[84px]">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row justify-between space-y-0 pb-2">
              <CardTitle className="text-sm">Total litros</CardTitle>
              <Icon name="droplet" className="text-muted-foreground" />
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5 h-[84px]">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row justify-between space-y-0 pb-2">
              <CardTitle className="text-sm">Total utilidad</CardTitle>
              <Icon name="credit-card" className="text-muted-foreground" />
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5 h-[84px]">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row justify-between space-y-0 pb-2">
              <CardTitle className="text-sm">Promedio utilidad</CardTitle>
              <Icon name="percent" className="text-muted-foreground" />
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5 h-[84px]">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-8 gap-4 h-full">
          {/* MonthlyEarningsChart */}
          <Card className="flex flex-col col-span-2 lg:col-span-5 w-full min-h-[300px] md:min-h-0">
            <CardHeader className="relative flex flex-row justify-between w-full pb-8">
              <CardTitle className="w-fit">Ingresos</CardTitle>
              <Skeleton className="absolute right-4 top-2 h-9 w-36" />
            </CardHeader>
            <CardContent className="h-full">
              <ChartContainer
                config={{
                  value: {
                    label: "Ganancias",
                    color: "hsl(var(--muted))",
                    icon: DollarSign,
                  },
                }}
                className="h-full w-full md:aspect-auto"
              >
                <BarChart data={placeholderData}>
                  <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    className="animate-pulse rounded-md opacity-70"
                    fill="hsl(var(--muted))"
                    isAnimationActive={false}
                  />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
          {/* RecentSalesTable */}
          <Card className="relative col-span-2 lg:col-span-3 w-full min-h-[450px] md:min-h-0 overflow-hidden">
            <CardHeader className="z-20 relative flex flex-row justify-between w-full">
              <CardTitle>Ventas recientes</CardTitle>
              <Skeleton className="absolute right-4 top-2 h-9 w-36" />
            </CardHeader>
            <CardContent className="absolute top-0 pt-[70px] h-full w-full max-h-full overflow-scroll">
              <div className="flex flex-col gap-3 w-full h-full pb-3">
                {Array(15)
                  .fill(null)
                  .map((_, index) => (
                    <RecentSaleSkeleton key={index} />
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const AdminDashboardSkeleton = () => {
  return (
    <div className="flex flex-col gap-10 p-6 md:px-20 md:py-14 animate-pulse">
      {/* First section */}
      <div className="grid items-start justify-between grid-cols-1 gap-y-10 md:gap-10 md:grid-cols-3">
        <div className="flex flex-col gap-10">
          {/* Welcome message */}
          <div className="flex flex-col gap-4">
            <div className="h-9 bg-accent/40 rounded w-72"></div>{" "}
            {/* "Bienvenido de vuelta, Alex" */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-accent/40 shadow-md"></div>{" "}
              {/* UserAvatar */}
              <div className="flex flex-col gap-1">
                <div className="h-5 bg-accent/40 rounded w-40"></div>{" "}
                {/* User name */}
                <div className="h-4 bg-accent/40 rounded w-32 text-sm"></div>{" "}
                {/* Role name */}
              </div>
            </div>
          </div>

          {/* Acceso rápido */}
          <div className="flex flex-col gap-4">
            <div className="h-7 bg-accent/40 rounded w-40"></div>{" "}
            {/* "Acceso rápido" */}
            <div className="grid items-start justify-center grid-cols-4 gap-4 md:max-w-80">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="flex flex-col gap-2 items-center justify-center text-xs"
                >
                  <div className="flex items-center justify-center rounded-2xl md:rounded-lg p-1.5 h-[72px] w-[72px] md:h-14 md:w-14 bg-muted shadow-md -mt-1">
                    <div className="w-7 h-7 md:w-6 md:h-6 bg-accent/40 rounded"></div>
                  </div>
                  <div className="h-4 bg-accent/40 rounded w-16 text-center"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Actividades recientes */}
          <div className="flex flex-col gap-5 mt-3">
            <div className="h-7 bg-accent/40 rounded w-52"></div>{" "}
            {/* "Actividades recientes" */}
            <div className="relative h-60">
              <div className="flex flex-col w-full gap-4">
                {Array(6)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {/* UserAvatar */}
                      <div className="h-6 w-6 min-w-6 min-h-6 rounded-full bg-rose-300/60" />
                      {/* Text */}
                      <div className="h-4 w-3/4 bg-muted rounded opacity-40" />
                    </div>
                  ))}
              </div>
              <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-background to-transparent h-20 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Charts - Only shown for admin */}
        <div className="flex flex-col col-span-2 gap-4 mt-1 max-w-screen">
          {/* Chart title and date */}
          <div className="flex flex-col gap-2 mb-4">
            <div className="h-7 bg-accent/40 rounded w-56"></div>{" "}
            {/* "Visualización del inventario" */}
            <div className="h-5 bg-accent/40 rounded w-32"></div> {/* Date */}
          </div>

          {/* Bar chart */}
          <div className="w-full aspect-video max-w-[720px] h-[400px] md:h-[65vh]">
            <div className="flex h-full flex-col">
              {/* Chart bars */}
              <div className="flex-1 flex items-end justify-between gap-4 mb-6">
                <div className="h-[70%] w-full bg-muted rounded opacity-90"></div>
                <div className="h-[50%] w-full bg-muted rounded opacity-90"></div>
                <div className="h-[30%] w-full bg-muted rounded opacity-90"></div>
                <div className="h-[80%] w-full bg-muted rounded opacity-90"></div>
                <div className="h-[40%] w-full bg-muted rounded opacity-90"></div>
                <div className="h-[100%] w-full bg-muted rounded opacity-90"></div>
              </div>

              {/* Chart labels */}
              <div className="grid grid-cols-7 gap-4">
                {[
                  "Etanol (MP)",
                  "Sellos (MP)",
                  "Gasolina Regular 87 (MP)",
                  "Nafta Aditivada (MP)",
                  "Colorante (MP)",
                  "Nafta Ligera (MP)",
                ].map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-accent/30 rounded text-center"
                  ></div>
                ))}
              </div>

              {/* Legend */}
              <div className="flex justify-center items-center mt-12">
                <div className="w-4 h-4 bg-accent/40 rounded mr-2"></div>
                <div className="h-4 bg-accent/40 rounded w-24"></div>{" "}
                {/* "Litros totales" */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DefaultSkeleton = () => {
  return <></>;
};

export function getGenericDataTableSkeleton(title: string, rowCount: number) {
  return (
    <div className="flex flex-col gap-6 md:gap-10 p-6 md:px-20 md:py-14">
      <h1 className="text-h1">{title}</h1>
      <DataTableSkeleton rows={rowCount} className="mt-1 rounded-xl" />
    </div>
  );
}

export function getGenericExpandableDataTableSkeleton(
  title: string,
  rowCount: number
) {
  return (
    <div className="flex flex-col gap-6 p-6 md:px-20 md:py-14">
      <div className="flex flex-col gap-y-4 justify-between">
        <h1 className="text-h1">{title}</h1>
        <CardDataTableSkeleton className="mt-16" rows={rowCount} />
      </div>
    </div>
  );
}

// @todo – Leverage the state to load the correct amount of cached rows
// when final move to Fuel Portfolio for all of these
export function ClientsRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/clients");
  if (!rowCount) {
    return <DefaultSkeleton />;
  }
  return getGenericDataTableSkeleton("Clientes", rowCount);
}

export function OrdersPurchasesRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/orders/purchases");
  if (!rowCount) {
    return <DefaultSkeleton />;
  }
  return getGenericExpandableDataTableSkeleton("Órdenes de compra", rowCount);
}

export function OrdersSalesRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/orders/sales");
  if (!rowCount) {
    return <DefaultSkeleton />;
  }
  return getGenericExpandableDataTableSkeleton("Órdenes de venta", rowCount);
}

export function ProductsRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/products");
  if (!rowCount) {
    return <DefaultSkeleton />;
  }
  return getGenericDataTableSkeleton("Productos", rowCount);
}

export function ProvidersRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/providers");
  if (!rowCount) {
    return <DefaultSkeleton />;
  }
  return getGenericDataTableSkeleton("Proveedores", rowCount);
}

export function InventoriesRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/inventories");

  if (!rowCount) {
    return <DefaultSkeleton />;
  }

  return (
    <div className="flex flex-col gap-6 md:gap-10 p-6 md:px-20 md:py-14">
      <h1 className="text-h1 md:h-9">Inventarios</h1>

      <div className="grid lg:grid-cols-8 gap-4">
        <Card className="lg:col-span-5">
          <CardHeader className="relative pb-8">
            <CardTitle>Productos puros (L) – Barras</CardTitle>
          </CardHeader>
          <CardContent className="h-[50vh] md:h-full aspect-auto md:aspect-video w-full"></CardContent>
        </Card>
        <Card className="flex flex-col lg:col-span-3">
          <CardHeader className="relative pb-8">
            <CardTitle>Productos puros (L) – Pastel</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center items-center h-[50vh] md:h-full aspect-auto md:aspect-video"></CardContent>
        </Card>
      </div>

      <h1 className="text-h1">Inventarios</h1>
      <DataTableSkeleton rows={rowCount} className="mt-1 rounded-xl" />
    </div>
  );
}

export function TerminalsRouteSkeleton() {
  const { rowCount: terminalsRowCount } = useTableSkeletonState(
    "/terminals?entity=terminals"
  );
  const { rowCount: externalRowCount } = useTableSkeletonState(
    "/terminals?entity=externalTerminals"
  );

  if (!terminalsRowCount) {
    return <DefaultSkeleton />;
  }

  return (
    <div className="flex flex-col gap-10 md:gap-16 p-6 md:px-20 md:py-14 animate-pulse">
      {/* Mis terminales section */}
      <div className="flex flex-col gap-10 min-h-[40vh] md:min-h-[50vh]">
        <div className="flex items-center justify-between">
          <h1 className="text-h1">Mis terminales</h1>
          <div className="h-9 w-28 bg-accent/40 rounded-md"></div>{" "}
          {/* "Agregar" button */}
        </div>

        {/* Terminal cards */}
        <div className="flex w-full overflow-x-auto pb-4">
          {Array(terminalsRowCount)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="flex items-start gap-4 md:gap-8 min-w-min"
              >
                {/* Single terminal card */}
                <div className="flex-shrink-0 flex flex-col gap-2 w-56 md:w-64 overflow-hidden p-3">
                  <div className="rounded-xl flex items-center justify-center w-full aspect-square bg-accent/10">
                    <div className="h-11 w-11 bg-accent/30 rounded"></div>{" "}
                    {/* Factory icon */}
                  </div>
                  <div className="flex flex-col">
                    <div className="h-5 bg-accent/40 rounded w-36 mb-2"></div>{" "}
                    {/* Terminal name */}
                    <div className="h-4 bg-accent/30 rounded w-full"></div>{" "}
                    {/* Address line 1 */}
                    <div className="h-4 bg-accent/30 rounded w-3/4 mt-1"></div>{" "}
                    {/* Address line 2 */}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Terminales externas section */}
      <div className="flex flex-col gap-10 min-h-[40vh]">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-y-4 justify-between">
          <div className="h-9 bg-accent/40 rounded w-56"></div>{" "}
          {/* "Terminales externas" */}
          <div className="flex justify-between gap-4 w-full md:w-auto">
            <div className="h-9 bg-accent/40 rounded w-10"></div>{" "}
            {/* Search filter */}
            <div className="h-9 w-24 bg-accent/40 rounded-md"></div>{" "}
            {/* "Agregar" button */}
          </div>
        </div>

        <DataTableSkeleton rows={externalRowCount || 5} />
      </div>
    </div>
  );
}

export function TerminalsIdRouteSkeleton({ terminal }: { terminal: Terminal }) {
  const { globalRole, teamRole } = useRoles();

  const rootPathname = `/terminals/${terminal.id}`;
  const inflowsPathname = `${rootPathname}/inflows`;
  const outflowsPathname = `${rootPathname}/outflows`;

  const isAdmin = getIsAdmin({ globalRole, teamRole });

  const terminalRoutes = isAdmin
    ? [
        { name: "Programa", to: rootPathname },
        { name: "Entradas", to: inflowsPathname },
        { name: "Salidas", to: outflowsPathname },
      ]
    : [{ name: "Programa", to: rootPathname }];

  const navigate = useNavigate();

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 bg-gradient-to-t from-muted/65 to-30% to-background pt-6 px-6 md:px-14 md:pt-12">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/terminals">Terminales</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {terminal.name} – <span>{terminal.address.short}</span>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header with back button and title */}
          <div className="flex items-center gap-4 text-h1">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-xl hover:scale-105"
            >
              <Icon name="arrow-left" className="text-foreground" />
            </button>
            <Icon name="factory" className="text-muted-foreground" />
            <h1>{terminal.name}</h1>
          </div>
        </div>

        {/* Navigation tabs */}
        <nav className="flex items-center gap-8">
          {terminalRoutes.map(({ to, name }) => {
            const isActive = name === "Programa";
            return (
              <NavLink
                key={to}
                to={to}
                prefetch="intent"
                preventScrollReset
                className={cn({
                  "py-4 text-sm text-muted-foreground": !isActive,
                  "text-sm text-foreground font-semibold underline underline-offset-[20px]":
                    isActive,
                })}
                end={to === rootPathname}
                viewTransition
              >
                {name}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Content area - top filters */}
      <div className="p-6 md:px-14 md:py-7 w-full animate-pulse">
        {/* <ProgramSkeleton /> */}
      </div>
    </div>
  );
}

export function DeliveriesRouteSkeleton() {
  const { rowCount } = useTableSkeletonState("/orders/sales");
  if (!rowCount) {
    return <DefaultSkeleton />;
  }
  return (
    <div className="flex flex-col gap-10 p-6 md:px-14 md:py-12">
      <div className="flex flex-col gap-y-4 justify-between">
        <h1 className="text-h1">Entregas</h1>
        <CardDataTableSkeleton className="mt-20" />
      </div>
    </div>
  );
}
